/**
 * Offline Banner Components
 * 
 * Reusable banner components to show offline status and cached data information
 */

import { AlertCircle, Wifi, WifiOff, RefreshCw, Clock } from 'lucide-react';
import { useNetworkStatus, useSyncStatus, useDataAvailability } from '@/contexts/offline';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';

interface OfflineBannerProps {
  className?: string;
  variant?: 'info' | 'warning' | 'error';
  showRefreshButton?: boolean;
  onRefresh?: () => void;
  customMessage?: string;
}

/**
 * Basic Offline Banner
 * Shows when user is offline or viewing cached data
 */
export function OfflineBanner({
  className,
  variant = 'info',
  showRefreshButton = true,
  onRefresh,
  customMessage
}: OfflineBannerProps) {
  const { isOnline } = useNetworkStatus();
  const { hasLocalData, isDataStale, dataLastUpdated } = useDataAvailability();

  // Don't show banner if online and data is fresh
  if (isOnline && !isDataStale) {
    return null;
  }

  const getVariantStyles = () => {
    switch (variant) {
      case 'warning':
        return {
          container: 'bg-yellow-50 border-yellow-200',
          text: 'text-yellow-800',
          icon: 'text-yellow-600'
        };
      case 'error':
        return {
          container: 'bg-red-50 border-red-200',
          text: 'text-red-800',
          icon: 'text-red-600'
        };
      default:
        return {
          container: 'bg-blue-50 border-blue-200',
          text: 'text-blue-800',
          icon: 'text-blue-600'
        };
    }
  };

  const styles = getVariantStyles();
  const Icon = !isOnline ? WifiOff : Clock;

  const getMessage = () => {
    if (customMessage) return customMessage;
    
    if (!isOnline) {
      return hasLocalData 
        ? 'You are offline. Viewing cached data from your last sync.'
        : 'You are offline. Some content may not be available.';
    }
    
    if (isDataStale) {
      const lastUpdate = dataLastUpdated 
        ? new Date(dataLastUpdated).toLocaleString()
        : 'unknown time';
      return `Viewing cached data from ${lastUpdate}. Refresh to get latest updates.`;
    }
    
    return 'Viewing cached data. Refresh to get latest updates.';
  };

  return (
    <Alert className={cn(styles.container, 'border', className)}>
      <Icon className={cn('h-4 w-4', styles.icon)} />
      <AlertDescription className={cn('flex items-center justify-between', styles.text)}>
        <span>{getMessage()}</span>
        {showRefreshButton && onRefresh && isOnline && (
          <Button
            variant="outline"
            size="sm"
            onClick={onRefresh}
            className="ml-4 h-8"
          >
            <RefreshCw className="h-3 w-3 mr-1" />
            Refresh
          </Button>
        )}
      </AlertDescription>
    </Alert>
  );
}

/**
 * Cached Data Banner
 * Specifically for showing when data is from cache
 */
export function CachedDataBanner({
  className,
  onRefresh,
  lastUpdated
}: {
  className?: string;
  onRefresh?: () => void;
  lastUpdated?: string;
}) {
  const { isOnline } = useNetworkStatus();

  const formatLastUpdated = () => {
    if (!lastUpdated) return 'unknown time';
    
    try {
      const date = new Date(lastUpdated);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffMins = Math.floor(diffMs / (1000 * 60));
      
      if (diffMins < 1) return 'just now';
      if (diffMins < 60) return `${diffMins} minutes ago`;
      
      const diffHours = Math.floor(diffMins / 60);
      if (diffHours < 24) return `${diffHours} hours ago`;
      
      const diffDays = Math.floor(diffHours / 24);
      return `${diffDays} days ago`;
    } catch {
      return 'unknown time';
    }
  };

  return (
    <div className={cn(
      'flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg',
      className
    )}>
      <div className="flex items-center gap-2">
        <Wifi className="h-4 w-4 text-blue-600" />
        <span className="text-sm text-blue-700">
          📱 Viewing cached data (last updated {formatLastUpdated()})
        </span>
      </div>
      {onRefresh && isOnline && (
        <Button
          variant="outline"
          size="sm"
          onClick={onRefresh}
          className="h-8"
        >
          <RefreshCw className="h-3 w-3 mr-1" />
          Refresh
        </Button>
      )}
    </div>
  );
}

/**
 * Sync Status Banner
 * Shows sync progress and status
 */
export function SyncStatusBanner({ className }: { className?: string }) {
  const { isSyncing, syncError } = useSyncStatus();
  const { isOnline } = useNetworkStatus();

  if (!isSyncing && !syncError) {
    return null;
  }

  if (syncError) {
    return (
      <Alert className={cn('bg-red-50 border-red-200', className)}>
        <AlertCircle className="h-4 w-4 text-red-600" />
        <AlertDescription className="text-red-800">
          Sync failed: {syncError}. {isOnline ? 'Will retry automatically.' : 'Will retry when back online.'}
        </AlertDescription>
      </Alert>
    );
  }

  if (isSyncing) {
    return (
      <Alert className={cn('bg-blue-50 border-blue-200', className)}>
        <RefreshCw className="h-4 w-4 text-blue-600 animate-spin" />
        <AlertDescription className="text-blue-800">
          Syncing your data...
        </AlertDescription>
      </Alert>
    );
  }

  return null;
}

/**
 * Connection Quality Banner
 * Shows when connection is poor
 */
export function ConnectionQualityBanner({ className }: { className?: string }) {
  const { connectionQuality, isOnline } = useNetworkStatus();

  if (!isOnline || connectionQuality !== 'poor') {
    return null;
  }

  return (
    <Alert className={cn('bg-yellow-50 border-yellow-200', className)}>
      <AlertCircle className="h-4 w-4 text-yellow-600" />
      <AlertDescription className="text-yellow-800">
        Poor connection detected. Sync operations may be slower than usual.
      </AlertDescription>
    </Alert>
  );
}
